import { LeftCircleFilled } from "@ant-design/icons";
import { Button, DatePicker, Layout, message, Switch, Table, Tag } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentBox } from "../../components/layout/content";
import { MyContent } from "../../components/layout/loginLayout";
import { HeaderTitle, MyModal } from "../../components/styled-components";
import { FormatDateYMDHMS } from "../../functions/function";
import { BatchItemLists } from "../../res/type";
import { PATCH, POST } from "../../services";
import {
  CHECKER_DOWNLOAD_BATCH_ITEM,
  CHECKER_GET_BATCH_ITEMS_FILTER,
  CHECKER_UPDATE_BATCH_ITEM_BY_ID,
} from "../../services/path";

const BatchItem = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().add(-1, "day"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [startDateRange, setStartDateRange] = useState<string>("");
  const [endDateRange, setEndDateRange] = useState<string>("");
  const latestStartDateRef = useRef(startDateRange);
  const latestEndDateRef = useRef(endDateRange);
  const [batchItemID, setBatchItemID] = useState<number>(0);
  const [downloadBatchItemID, setDownloadBatchItemID] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isModalPreview, setIsModalPreview] = useState(false);
  const [filePath, setFilePath] = useState<string>("");
  const [data, setData] = useState<BatchItemLists[]>([]);
  const { RangePicker } = DatePicker;

  useEffect(() => {
    fetchDefaultDate();
    let intervalId: NodeJS.Timeout | null = null;
    const timeoutId = setTimeout(() => {
      fetchData(true); // first fetch at x.01, x.11, ...
      intervalId = setInterval(
        () => {
          fetchData(true); // repeat every 10 mins
        },
        6 * 10 * 10 * 1000,
      ); // 10 minutes
    }, getDelayUntilNextTargetTime());

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    fetchDownloadBatchItemPDF();
  }, [downloadBatchItemID]);

  useEffect(() => {
    fetchUpdatePatchIsChecked();
  }, [batchItemID]);

  useEffect(() => {
    if (downloadBatchItemID !== 0 && filePath !== "") {
      handleOpenPreviewPDFModal();
    }
  }, [filePath]);

  useEffect(() => {
    fetchData(true);
  }, [startDateRange, endDateRange]);

  const fetchDefaultDate = () => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);
    const formattedStartDate = formatDate(currentDate);
    latestStartDateRef.current = formattedStartDate;
    setStartDateRange(formattedStartDate);
    const nextDayDate = new Date(currentDate);
    nextDayDate.setDate(currentDate.getDate() + 1);
    const formattedEndDate = formatDate(nextDayDate);
    latestEndDateRef.current = formattedEndDate;
    setEndDateRange(formattedEndDate);
  };

  const fetchData = async (noti: boolean) => {
    if (latestStartDateRef.current === "" || latestEndDateRef.current === "") return;
    try {
      setData([]);
      const requestBody = {
        start_date: latestStartDateRef.current,
        end_date: latestEndDateRef.current,
      };

      const response = await POST(CHECKER_GET_BATCH_ITEMS_FILTER, requestBody);
      if (noti) {
        if (response.result.length > 0) {
          message.success("ดึงข้อมูลสำเร็จ");
        } else {
          message.warning("ไม่พบข้อมูล");
        }
      }

      setData(response.result);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchDownloadBatchItemPDF = async () => {
    if (downloadBatchItemID === 0) return;
    try {
      const requestBody = {
        id: downloadBatchItemID,
      };

      const response = await POST(CHECKER_DOWNLOAD_BATCH_ITEM, requestBody);
      if (response.success) {
        message.success("ดึงข้อมูลสำเร็จ");
      } else {
        message.warning("ไม่พบข้อมูล");
      }

      setFilePath(response?.result?.file_path ?? "");
      handleOpenPreviewPDFModal();
      setDownloadBatchItemID(0);
      fetchData(false);
    } catch (error) {
      message.warning("ไม่พบข้อมูล");
      setDownloadBatchItemID(0);
      console.error("Error fetching data:", error);
    }
  };

  const fetchUpdatePatchIsChecked = async () => {
    if (batchItemID === 0) return;
    try {
      const response = await PATCH(CHECKER_UPDATE_BATCH_ITEM_BY_ID(batchItemID), null);
      if (response.success) {
        message.success("ตรวจสอบข้อมูลสำเร็จ");
      } else {
        message.warning("ไม่พบข้อมูล");
      }

      setBatchItemID(0);
      fetchData(false);
    } catch (error) {
      message.warning("ไม่พบข้อมูล");
      setBatchItemID(0);
      console.error("Error fetching data:", error);
    }
  };

  function getDelayUntilNextTargetTime(): number {
    const now = new Date();
    const minutes = now.getMinutes();

    // Find the next minute that ends in .01, .11, .21, ...
    const nextTargetMinute = Math.floor(minutes / 10) * 10 + 1;
    let targetMinute = nextTargetMinute;
    if (minutes >= nextTargetMinute) {
      // ถ้านาทีตอนนี้เลยหรือเท่ากับ target ให้ขยับไปอีก 10 นาที
      targetMinute += 10;
      if (targetMinute >= 60) {
        targetMinute -= 60; // ถ้าเกิน 59 นาที ให้กลับมาเริ่มที่ 0
      }
    }

    const nextTargetTime = new Date(now);
    nextTargetTime.setMinutes(targetMinute);
    nextTargetTime.setSeconds(1); // .01 seconds
    nextTargetTime.setMilliseconds(0);

    // If that time has already passed, add 10 minutes
    if (nextTargetTime <= now) {
      nextTargetTime.setMinutes(nextTargetTime.getMinutes() + 10);
    }

    // ⏱ Return the time difference in milliseconds
    return nextTargetTime.getTime() - now.getTime();
  }

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleBackButton = () => {
    navigate("/login");
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: string[]) => {
    if (dates) {
      if (typeof dateStrings[0] === "string") {
        setStartDateRange(dateStrings[0]);
        setEndDateRange(dateStrings[1]);
        setStartDate(dates[0]);
        setEndDate(dates[1]);
        latestStartDateRef.current = dateStrings[0];
        latestEndDateRef.current = dateStrings[1];
      }
    }
  };

  const handleIsChecked = (record: BatchItemLists) => {
    setBatchItemID(record.id);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePreviewPDF = (record: BatchItemLists) => {
    setDownloadBatchItemID(record.id);
  };

  const handleOpenPreviewPDFModal = () => {
    setIsModalPreview(true);
  };

  const handleCloseModal = () => {
    setIsModalPreview(false);
  };

  const columns = [
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            minWidth: 125,
          }}
        >
          Batch No
        </div>
      ),
      dataIndex: "batch_no",
      key: "batch_no",
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            minWidth: 125,
          }}
        >
          Logistic
        </div>
      ),
      dataIndex: "logistic",
      key: "logistic",
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            minWidth: 125,
          }}
        >
          Brand
        </div>
      ),
      dataIndex: "brand_name",
      key: "brand_name",
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          สถานะการพิมพ์
        </div>
      ),
      dataIndex: "is_printed",
      key: "is_printed",
      render: (isPrinted: boolean) =>
        isPrinted ? (
          <div style={{ textAlign: "center" }}>
            <Tag color="processing">พิมพ์แล้ว</Tag>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <Tag color="warning">รอพิมพ์</Tag>
          </div>
        ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          สถานะการตรวจสอบ
        </div>
      ),
      key: "is_checked",
      render: (record: BatchItemLists) =>
        record?.is_printed ? (
          <div
            style={{
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {record?.is_checked ? (
              <Tag color="success">ตรวจสอบแล้ว</Tag>
            ) : (
              <Tag color="warning">รอดำเนินการ</Tag>
            )}
            <Switch
              style={{ backgroundColor: record?.is_checked ? "#ff4d4f" : "" }}
              disabled={record?.is_checked}
              defaultChecked={record?.is_checked ?? false}
              onChange={() => handleIsChecked(record)}
            />
          </div>
        ) : null,
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          จำนวน (แผ่น)
        </div>
      ),
      dataIndex: "total_pages",
      key: "total_pages",
      render: (text: number) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            minWidth: 150,
          }}
        >
          Date
        </div>
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{FormatDateYMDHMS(text.replace("T", " ").replace("Z", ""))}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          Print
        </div>
      ),
      key: "action",
      render: (record: BatchItemLists) =>
        !record?.is_checked ? (
          <div style={{ textAlign: "center" }}>
            <Button type="primary" onClick={() => handlePreviewPDF(record)}>
              Preview
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <MyContent>
        <Button
          icon={<LeftCircleFilled />}
          style={{ marginBottom: 10, fontSize: 20, border: 0 }}
          size={"large"}
          onClick={handleBackButton}
        >
          Back
        </Button>
        <ContentBox>
          <div style={{ textAlign: "center" }}>
            <img
              src={process.env.REACT_APP_FRONTEND_URL + "/logo_uplift_3.jpg"}
              style={{
                width: "250px",
                objectFit: "cover",
              }}
              alt={process.env.REACT_APP_FRONTEND_URL + "/logo_uplift_3.jpg"}
            />
          </div>
          <HeaderTitle>
            <h2>Batch Instant</h2>
          </HeaderTitle>
          <RangePicker
            value={[startDate, endDate]}
            onChange={handleRangeChange}
            format="YYYY-MM-DD"
            allowClear
          />
          <Table
            columns={columns}
            dataSource={data}
            scroll={{ x: 1500 }}
            rowKey="id"
            pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onShowSizeChange: (_, size) => handlePageSizeChange(size),
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              total: data.length,
            }}
            style={{ marginTop: 10 }}
          />
          {isModalPreview && (
            <MyModal
              title="PDF Viewer"
              open={isModalPreview}
              onOk={handleCloseModal}
              onCancel={handleCloseModal}
              footer={null}
            >
              {filePath !== "" ? (
                <iframe src={filePath} width="100%" height="500px" title="PDF Preview"></iframe>
              ) : (
                <p>Loading PDF...</p>
              )}
            </MyModal>
          )}
        </ContentBox>
      </MyContent>
    </Layout>
  );
};

export default BatchItem;
